import { api } from "../../api";
import {
  currentSamples,
  fieldHasMaps,
  type Section180FieldInput,
} from "./section180";
import { stateOfPoint } from "../../lib/usStates";
import type {
  DashboardField,
  LayerMeta,
  SampleEvent,
} from "../../types/contract";

async function zonesForEvent(
  fieldId: string,
  layers: LayerMeta[],
  event: SampleEvent | null,
): Promise<GeoJSON.FeatureCollection | null> {
  if (event?.kind !== "zones") return null;
  // the zones the current maps came from: the event's own target-zones layer, else the saved zones
  const zl =
    layers.find((l) => l.id === `target-zones-${event.event_id}`) ??
    layers.find((l) => l.id === "zones");
  if (!zl) return null;
  try {
    const detail = await api.fetchLayerDetail(fieldId, zl.id);
    return (detail.inline ?? null) as GeoJSON.FeatureCollection | null;
  } catch {
    return null;
  }
}

async function inputFor(
  field: DashboardField,
): Promise<Section180FieldInput | null> {
  const [samples, events] = await Promise.all([
    api.fetchSoilSamples(field.field_id),
    api.fetchSampleEvents(field.field_id),
  ]);
  const { samples: cur, event } = currentSamples(samples, events);
  const valued = cur.filter((s) => fieldHasMaps([s]));
  if (!valued.length) return null;
  // The list names the zones row. Its geometry, and the boundary thumbnail,
  // are read on their own — the report does not download every layer.
  const listed = await api.fetchFieldLayerList(field.field_id);
  const layers = listed.document?.layers ?? [];
  const state =
    (await stateOfPoint(field.centroid[0], field.centroid[1])) ?? "NA";
  return {
    field: {
      field_id: field.field_id,
      field_name: field.field_name,
      acres: field.acres,
      state,
    },
    samples: valued,
    eventKind: event?.kind ?? null,
    zonesFc: await zonesForEvent(field.field_id, layers, event),
    boundary: listed.document?.boundary ?? null,
  };
}

// ALL selected fields must qualify. A report that silently skips a field is worse than being
// told to run Map Soil first. One input per qualifying field, plus the names that cannot be
// reported yet. Sequential, same as the batch runner, so the burst stays gentle.
export async function assembleSection180Inputs(
  selected: DashboardField[],
): Promise<{ inputs: Section180FieldInput[]; lacking: string[] }> {
  const inputs: Section180FieldInput[] = [];
  const lacking: string[] = [];
  for (const field of selected) {
    const input = await inputFor(field);
    if (input) inputs.push(input);
    else lacking.push(field.field_name);
  }
  return { inputs, lacking };
}
